import Select from 'react-select';
import { Control, Controller } from 'react-hook-form';
import { DeploymentRequirements, DeviceOption } from 'interfaces/deployment';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo } from 'react';
import { debounce } from 'lodash';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ReactQueryKeys } from 'constants/react-query-keys';
import { toast } from 'react-hot-toast';
import { PaginationState, ColumnFiltersState } from '@tanstack/react-table';
import DevicesService from 'services/DevicesService';

/**
 * @description Component for selecting devices for deployment with search and pagination capabilities.
 *
 * @SADtrace SAD-003-6.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {Object} props - Component props
 * @param {Control<DeploymentRequirements, any>} props.control - React Hook Form control object
 * @param {boolean} props.requiredError - Flag indicating if a required field error should be shown
 * @param {boolean} props.libraryHasNotChangedSinceLastDeployment - Flag to disable selection if library hasn't changed
 *
 * @returns {JSX.Element} Rendered component
 */
const DeviceSelector = ({
  control,
  requiredError,
  libraryHasNotChangedSinceLastDeployment,
}: {
  control: Control<DeploymentRequirements, any>;
  requiredError: boolean;
  libraryHasNotChangedSinceLastDeployment: boolean;
}) => {
  const { t } = useTranslation();
  const [devices, setDevices] = useState<DeviceOption[]>([]);

  /**
   * @description Debounced function to handle search input changes.
   *
   * @SADtrace SAD-003-6.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string} inputValue - The search input value
   */
  const handleInputChange = useMemo(
    () =>
      debounce((inputValue: string) => {
        setColumnFilters([{ id: 'name', value: inputValue }]);
      }, 500),
    [],
  );

  // Cancel any pending debounce on unmount so it can't call setState after the
  // component (or the jsdom test environment) has been torn down.
  useEffect(() => () => handleInputChange.cancel(), [handleInputChange]);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  /**
   * @description Query to fetch devices with pagination and filtering
   */
  const { data, isLoading, isFetching, failureReason } = useQuery({
    queryKey: [
      ReactQueryKeys.DEVICES,
      {
        pageIndex,
        pageSize,
        columnFilters,
      },
    ],

    queryFn: () =>
      DevicesService.getAll({
        pageIndex,
        pageSize,
        columnFilters,
      }),

    placeholderData: keepPreviousData,
  });

  // Accumulate paginated devices. Idempotent via id-dedup; placeholderData keeps the
  // prior page reference during fetch, so there is no double-append.
  useEffect(() => {
    if (!data) return;
    setDevices((prevDevices) => {
      const newDevices = data.data.records.map((dev) => ({
        ...dev,
        value: dev.name,
        label: dev.name,
      }));
      // Filter out any devices that already exist in the previous data
      return [...prevDevices, ...newDevices].filter(
        (device, index, self) =>
          index === self.findIndex((d) => d.id === device.id),
      );
    });
  }, [data]);

  useEffect(() => {
    if (failureReason) {
      toast.error(
        (failureReason as any).response?.data?.message || t('errors.default'),
      );
    }
  }, [failureReason]);

  /**
   * @description Handles pagination when scrolling to the bottom of the dropdown.
   *
   * @SADtrace SAD-003-6.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {number} pageUpdated - The new page index to load
   */
  // v8 ignore start -- WAIVER(THIRDPARTY): infinite-scroll pagination callback invoked only by react-select onMenuScrollToBottom; jsdom cannot produce menu scroll geometry, so the total-pages guard and setPagination updater never run in unit tests; selector pagination verified via E2E
  const handlePageChange = (pageUpdated: number) => {
    if (
      typeof data?.data.totalPages === 'undefined' ||
      pageUpdated >= data?.data.totalPages
    ) {
      return;
    }

    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: pageUpdated,
    }));
  };
  // v8 ignore stop -- WAIVER(THIRDPARTY)

  return (
    <div className="pb-3">
      <label htmlFor="devices_selected" className="form-label">
        Select Device
      </label>
      <Controller
        control={control}
        name="devices_selected"
        rules={{
          required: true,
        }}
        render={({ field: { onChange, value, ref } }) => (
          <div
            style={{
              cursor: libraryHasNotChangedSinceLastDeployment
                ? 'not-allowed'
                : 'auto',
            }}
          >
            <Select
              id="devices"
              maxMenuHeight={300}
              ref={ref}
              // v8 ignore start -- WAIVER(DEFENSIVE): Array.prototype.slice always returns an array, so the ?? [] fallback branch is unreachable defensive coding kept for type safety of the RHF onChange payload
              onChange={(data) => {
                return onChange(data.slice(-1) ?? []);
              }}
              // v8 ignore stop -- WAIVER(DEFENSIVE)
              onInputChange={handleInputChange}
              // v8 ignore start -- WAIVER(THIRDPARTY): menu-scroll pagination trigger executed by react-select menu internals; jsdom cannot produce menu scroll geometry; selector behavior verified via E2E
              onMenuScrollToBottom={() => handlePageChange(pageIndex + 1)}
              // v8 ignore stop -- WAIVER(THIRDPARTY)
              isLoading={isLoading || isFetching}
              /*    closeMenuOnSelect={false} */
              value={value as any}
              isMulti
              className={`react-select-container-multiselector react-select-container`}
              classNamePrefix="react-select"
              isClearable
              isSearchable
              aria-label="devices"
              hideSelectedOptions={false}
              placeholder={t('deployment.form.device_selector_placeholder')}
              options={devices}
              isDisabled={libraryHasNotChangedSinceLastDeployment}
            />
          </div>
        )}
      />
      {/* v8 ignore start -- WAIVER(MICRO): conditional error-feedback artifact; the branch renders a translated required-devices message only, asserted at DeploymentForm level and via E2E */}
      {requiredError && (
        <div className="invalid-feedback">
          {t('deployment.errors.select__devices')}
        </div>
      )}
      {/* v8 ignore stop -- WAIVER(MICRO) */}
    </div>
  );
};

export default DeviceSelector;
